import React from "react";
import { motion } from "framer-motion";
import { Building2, UserX, Users, BoxIcon } from "lucide-react";
const alternatives = [{
  number: '01',
  icon: Building2,
  title: 'The National Franchise',
  desc: "You called a big national company and the quote made your jaw drop. And they still won't touch demolition or the heavy specialty items.",
  verdict: 'Overpriced. Half the job.'
}, {
  number: '02',
  icon: BoxIcon,
  title: 'The Dumpster Rental',
  desc: 'You rented a dumpster and realized — wait, I still have to do all the loading, the heavy lifting, and the sorting myself.',
  verdict: 'You do all the work.'
}, {
  number: '03',
  icon: UserX,
  title: 'The Buddy With A Truck',
  desc: 'You asked a friend and he flaked. Or you keep telling yourself you\'ll rent a truck and handle it "next weekend."',
  verdict: 'Still sitting there.'
}];
export function FailedAlternatives() {
  return <section className="py-20 lg:py-28 bg-white border-y border-gray-200" data-id="element-79" data-mp="2t8nk4mnkvbrb">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-80" data-mp="eoijsko3qx25">
        {/* Heading */}
        <div className="max-w-3xl mb-14" data-id="element-81" data-mp="em6125aisio1">
          <motion.span initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-[11px] tracking-[0.25em] font-bold uppercase text-[#C8102E] block mb-4" data-id="element-82" data-mp="anykpbjyxjdd">
            What Waiting Actually Costs
          </motion.span>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: [0.23, 1, 0.32, 1]
        }} className="text-4xl md:text-5xl font-bold text-[#1A1A1A] leading-[1.05] mb-6" data-id="element-83" data-mp="3rx533hucd6sn">
            THE JUNK ISN'T JUST IN YOUR SPACE.
            <br data-id="element-84" data-mp="396n9lb1oq768" />
            <span className="text-[#C8102E]" data-id="element-85" data-mp="345g1tbzjl7zk">IT'S BLOCKING YOUR LIFE.</span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.08
        }} className="text-lg text-gray-600 leading-relaxed" data-id="element-86" data-mp="2uhu2ohabscut">
            You stop inviting people over because of the garage. You can't park
            the car inside. You can't list the property until it's clean. The
            contractor can't start until the debris is gone. Every week it sits,
            it costs you more — in stress, in time, in property value.
          </motion.p>
        </div>

        {/* Failed attempts */}
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-gray-400 mb-6" data-id="element-87" data-mp="34fi5lui34qo2">
          You've probably already tried
        </p>

        <div className="grid md:grid-cols-3 gap-5 mb-16" data-id="element-88" data-mp="21ws9m29n6f">
          {alternatives.map((alt, index) => {
          const Icon = alt.icon;
          return <motion.article key={alt.number} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08,
            duration: 0.45,
            ease: [0.23, 1, 0.32, 1]
          }} className="group relative flex flex-col bg-[#F9F9F9] rounded-2xl border border-gray-200 p-7 hover:border-gray-300 hover:shadow-lg transition-[box-shadow,border-color]" data-id="element-89" data-mp="4tbjp2g86cem" data-mpi={index}>
                <div className="flex items-start justify-between mb-6" data-id="element-90" data-mp="16m5ph74xsfih">
                  <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-400 group-hover:text-[#1A1A1A] transition-colors" data-id="element-91" data-mp="11m9u1wab1wej">
                    <Icon size={22} data-id="element-92" data-mp="1biwckef5sbde" />
                  </div>
                  <span className="font-oswald text-4xl font-bold text-gray-200 leading-none" data-id="element-93" data-mp="2rxe5md5ddz4c">
                    {alt.number}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-[#1A1A1A] mb-3" data-id="element-94" data-mp="2my1ma2dpja6i">
                  {alt.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-[15px] mb-6" data-id="element-95" data-mp="11gal2h8vikor">
                  {alt.desc}
                </p>

                <div className="mt-auto pt-5 border-t border-gray-200" data-id="element-96" data-mp="15hme6wqyho1p">
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#C8102E]" data-id="element-97" data-mp="1agz1filqox4t">
                    {alt.verdict}
                  </span>
                </div>
              </motion.article>;
        })}
        </div>

        {/* Verdict */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative overflow-hidden bg-[#0E0E0E] rounded-3xl p-10 md:p-16" data-id="element-98" data-mp="1kdljjzmola35">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#C8102E] opacity-[0.12] blur-[130px] rounded-full pointer-events-none" data-id="element-99" data-mp="1y4gmyweeuyzc" />

          <div className="relative max-w-4xl" data-id="element-100" data-mp="2d9urml7eo8dm">
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed mb-8" data-id="element-101" data-mp="2daw0qk4v2ksp">
              Most junk companies just haul. They show up with a truck, load it,
              and leave. But what about the concrete slab that needs breaking
              up? The shed that has to come down first? The dirt, the green
              waste, the old fence, the piano nobody can lift, the jacuzzi
              that's been out back since 2018?
            </p>

            <div className="flex items-start gap-5" data-id="element-102" data-mp="2r0pspbbnxl6n">
              <div className="hidden sm:flex flex-shrink-0 w-14 h-14 rounded-2xl bg-[#C8102E] items-center justify-center" data-id="element-103" data-mp="2r3byb5eztqm8">
                <Users size={26} className="text-white" data-id="element-104" data-mp="2i8xj12ugqg34" />
              </div>
              <h3 className="text-3xl md:text-5xl font-bold text-white leading-[1.05]" data-id="element-105" data-mp="2i6uw4rolx32m">
                A TRUCK AND TWO GUYS CAN'T FIX THAT.
                <br data-id="element-106" data-mp="39zlogr3667ml" />
                <span className="text-[#C8102E]" data-id="element-107" data-mp="39yaickm94nsb">YOU NEED A CREW.</span>
              </h3>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}